import { useState } from 'react';
import { Upload, Database, FileJson, Check, AlertCircle, Loader2, FolderUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Collection-Konfiguration mit Beschreibungen
const COLLECTIONS = [
  { value: 'hausliste', label: 'Hausliste', description: 'Objekte / Ferienwohnungen' },
  { value: 'users', label: 'Benutzer', description: 'Admin- und Mitarbeiter-Konten' },
  { value: 'knowledge', label: 'Wissen (neu)', description: 'Wissenseinträge (neue Struktur)' },
  { value: 'knowledge_entries', label: 'Wissen (alt)', description: 'Wissenseinträge (alte Struktur)' },
  { value: 'settings', label: 'Einstellungen', description: 'App-Einstellungen' },
  { value: 'documents', label: 'Dokumente', description: 'Hochgeladene Dokumente' },
  { value: 'open_questions', label: 'Offene Fragen', description: 'Unbeantwortete Fragen' },
  { value: 'chat_history', label: 'Chat-Verlauf', description: 'Gespeicherte Chats' },
];

// Dateiname zu Collection Mapping
const FILENAME_MAPPING = {
  'hausliste': 'hausliste',
  'users': 'users',
  'knowledge_entries': 'knowledge_entries',
  'knowledge': 'knowledge',
  'settings': 'settings',
  'documents': 'documents',
  'open_questions': 'open_questions',
  'chat_history': 'chat_history',
};

export const ImportPage = () => {
  const [selectedCollection, setSelectedCollection] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Batch-Import States
  const [batchFiles, setBatchFiles] = useState([]);
  const [batchResults, setBatchResults] = useState([]);
  const [batchImporting, setBatchImporting] = useState(false);

  const getToken = () => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  // Einzelner Import
  const handleSingleImport = async () => {
    if (!selectedCollection || !selectedFile) {
      setError('Bitte Collection und Datei auswählen');
      return;
    }

    const token = getToken();
    if (!token) {
      setError('Nicht angemeldet');
      return;
    }

    setImporting(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API}/backup/import-collection?collection=${selectedCollection}`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Import fehlgeschlagen');
    } finally {
      setImporting(false);
    }
  };

  // Batch-Import: Dateien hinzufügen
  const handleBatchFilesSelect = (e) => {
    const files = Array.from(e.target.files);
    
    const mappedFiles = files.map(file => {
      // Versuche Collection aus Dateinamen zu erkennen
      const filename = file.name.toLowerCase();
      let detectedCollection = '';
      
      for (const [key, value] of Object.entries(FILENAME_MAPPING)) {
        if (filename.includes(key)) {
          detectedCollection = value;
          break;
        }
      }
      
      return {
        file,
        collection: detectedCollection,
        status: 'pending'
      };
    });
    
    setBatchFiles(mappedFiles);
    setBatchResults([]);
  };

  // Batch-Import: Collection für Datei ändern
  const updateBatchFileCollection = (index, collection) => {
    const updated = [...batchFiles];
    updated[index].collection = collection;
    setBatchFiles(updated);
  };

  // Batch-Import: Alle importieren
  const handleBatchImport = async () => {
    const token = getToken();
    if (!token) {
      setError('Nicht angemeldet');
      return;
    }

    // Prüfe ob alle Collections zugewiesen sind
    const unassigned = batchFiles.filter(f => !f.collection);
    if (unassigned.length > 0) {
      setError(`${unassigned.length} Datei(en) ohne Collection-Zuweisung`);
      return;
    }

    setBatchImporting(true);
    setError(null);
    const results = [];

    for (let i = 0; i < batchFiles.length; i++) {
      const { file, collection } = batchFiles[i];
      
      // Update Status
      const updated = [...batchFiles];
      updated[i].status = 'importing';
      setBatchFiles(updated);

      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${API}/backup/import-collection?collection=${collection}`,
          formData,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        results.push({
          filename: file.name,
          collection,
          success: true,
          ...response.data
        });

        // Update Status
        updated[i].status = 'success';
        setBatchFiles([...updated]);

      } catch (err) {
        results.push({
          filename: file.name,
          collection,
          success: false,
          error: err.response?.data?.detail || 'Fehler'
        });

        // Update Status
        updated[i].status = 'error';
        setBatchFiles([...updated]);
      }
    }

    setBatchResults(results);
    setBatchImporting(false);
  };

  return (
    <div className="min-h-full bg-gray-900 p-6" data-testid="import-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Database className="h-8 w-8 text-blue-400" />
            <h1 className="text-2xl font-bold text-white">Daten-Import</h1>
          </div>
          <p className="text-gray-400">
            Importieren Sie JSON-Dateien direkt in die Datenbank. 
            Bestehende Einträge werden aktualisiert (Upsert), niemals gelöscht.
          </p>
        </div>

        {/* Fehler-Anzeige */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Einzelner Import */}
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FileJson className="h-5 w-5 text-blue-400" />
            Einzelne Datei importieren
          </h2>

          <div className="space-y-4">
            {/* Collection Auswahl */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                1. Collection auswählen
              </label>
              <select
                value={selectedCollection}
                onChange={(e) => setSelectedCollection(e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="collection-select"
              >
                <option value="">-- Collection wählen --</option>
                {COLLECTIONS.map(coll => (
                  <option key={coll.value} value={coll.value}>
                    {coll.label} ({coll.description})
                  </option>
                ))}
              </select>
            </div>

            {/* Datei Auswahl */}
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                2. JSON-Datei auswählen
              </label>
              <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 border border-gray-600 border-dashed rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                <Upload className="h-5 w-5 text-gray-400" />
                <span className="text-gray-300">
                  {selectedFile ? selectedFile.name : 'Datei auswählen (.json)'}
                </span>
                <input
                  type="file"
                  accept=".json"
                  onChange={(e) => setSelectedFile(e.target.files[0])}
                  className="hidden"
                  data-testid="file-input"
                />
              </label>
            </div>

            {/* Import Button */}
            <button
              onClick={handleSingleImport}
              disabled={importing || !selectedCollection || !selectedFile}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              data-testid="import-btn"
            >
              {importing ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Importiere...
                </>
              ) : (
                <>
                  <Upload className="h-5 w-5" />
                  Import starten
                </>
              )}
            </button>
          </div>

          {/* Ergebnis */}
          {result && (
            <div className="mt-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Check className="h-5 w-5 text-green-400" />
                <span className="text-green-400 font-medium">Import erfolgreich!</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-gray-400">Collection:</div>
                <div className="text-white font-medium">{result.collection}</div>
                <div className="text-gray-400">In Datei:</div>
                <div className="text-white">{result.documents_in_file}</div>
                <div className="text-gray-400">Eingefügt:</div>
                <div className="text-green-400">{result.inserted}</div>
                <div className="text-gray-400">Aktualisiert:</div>
                <div className="text-yellow-400">{result.updated}</div>
                <div className="text-gray-400">Übersprungen:</div>
                <div className="text-gray-400">{result.skipped}</div>
                <div className="text-gray-400">Gesamt in DB:</div>
                <div className="text-white font-bold">{result.final_count}</div>
              </div>
            </div>
          )}
        </div>

        {/* Batch Import */}
        <div className="bg-gray-800 rounded-lg p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <FolderUp className="h-5 w-5 text-green-400" />
            Mehrere Dateien importieren
          </h2>

          <div className="space-y-4">
            {/* Mehrere Dateien auswählen */}
            <div>
              <label className="flex items-center justify-center gap-2 px-4 py-4 bg-gray-700 border border-gray-600 border-dashed rounded-lg cursor-pointer hover:bg-gray-600 transition-colors">
                <FolderUp className="h-6 w-6 text-gray-400" />
                <span className="text-gray-300">
                  Mehrere JSON-Dateien auswählen
                </span>
                <input
                  type="file"
                  accept=".json"
                  multiple
                  onChange={handleBatchFilesSelect}
                  className="hidden"
                  data-testid="batch-file-input"
                />
              </label>
            </div>

            {/* Datei-Liste mit Collection-Zuweisung */}
            {batchFiles.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm text-gray-400 mb-2">
                  Bitte Collection für jede Datei zuweisen:
                </p>
                {batchFiles.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 p-3 bg-gray-700 rounded-lg">
                    {/* Status Icon */}
                    <div className="flex-shrink-0">
                      {item.status === 'pending' && <FileJson className="h-5 w-5 text-gray-400" />}
                      {item.status === 'importing' && <Loader2 className="h-5 w-5 text-blue-400 animate-spin" />}
                      {item.status === 'success' && <Check className="h-5 w-5 text-green-400" />}
                      {item.status === 'error' && <AlertCircle className="h-5 w-5 text-red-400" />}
                    </div>
                    
                    {/* Dateiname */}
                    <div className="flex-1 text-sm text-white truncate" title={item.file.name}>
                      {item.file.name}
                    </div>
                    
                    {/* Collection Dropdown */}
                    <select
                      value={item.collection}
                      onChange={(e) => updateBatchFileCollection(index, e.target.value)}
                      className="bg-gray-600 border border-gray-500 rounded px-2 py-1 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={batchImporting}
                    >
                      <option value="">-- wählen --</option>
                      {COLLECTIONS.map(coll => (
                        <option key={coll.value} value={coll.value}>
                          {coll.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}

                {/* Batch Import Button */}
                <button
                  onClick={handleBatchImport}
                  disabled={batchImporting || batchFiles.some(f => !f.collection)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors mt-4"
                  data-testid="batch-import-btn"
                >
                  {batchImporting ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Importiere alle...
                    </>
                  ) : (
                    <>
                      <FolderUp className="h-5 w-5" />
                      Alle {batchFiles.length} Dateien importieren
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Batch Ergebnisse */}
            {batchResults.length > 0 && (
              <div className="mt-4 p-4 bg-gray-700 rounded-lg">
                <h3 className="text-white font-medium mb-3">Import-Ergebnisse:</h3>
                <div className="space-y-2">
                  {batchResults.map((res, index) => (
                    <div key={index} className={`p-3 rounded-lg ${res.success ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                      <div className="flex items-center gap-2 mb-1">
                        {res.success ? (
                          <Check className="h-4 w-4 text-green-400" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-400" />
                        )}
                        <span className={res.success ? 'text-green-400' : 'text-red-400'}>
                          {res.filename} → {res.collection}
                        </span>
                      </div>
                      {res.success ? (
                        <div className="text-sm text-gray-300 ml-6">
                          {res.inserted} eingefügt, {res.updated} aktualisiert, {res.final_count} gesamt
                        </div>
                      ) : (
                        <div className="text-sm text-red-300 ml-6">
                          {res.error}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Hinweis */}
        <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
          <p className="text-sm text-blue-300">
            <strong>Hinweis:</strong> Der Import verwendet "Upsert" - bestehende Einträge 
            werden aktualisiert (basierend auf ID), neue Einträge werden hinzugefügt. 
            Es werden niemals Daten gelöscht.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImportPage;
